'use client'

import type { BarShapeProps } from 'recharts'
import { Bar, BarChart, CartesianGrid, Rectangle, XAxis, YAxis } from 'recharts'

import type { RouterOutputs } from '@package/api'
import type { ChartConfig } from '@ui/web/components'
import { ChartContainer, ChartTooltip, ChartTooltipContent, NumberFlow } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { starOnLessonArray } from '@helper/utils/business-logic'
import cn from '@config/tailwind/cn'

import { RewardStar } from '../../../components/reward-star'

type ActivityByDay = RouterOutputs['metrics']['getLessonTrend']['activityByDay']
type Population = RouterOutputs['metrics']['getLessonTrend']['population']

const ACTIVITY_CONFIG = {
  assignedXp: { label: 'Tugas (guru)', color: 'var(--chart-1)' },
  selfXp: { label: 'Latihan (mandiri)', color: 'var(--chart-5)' },
  // Badge awards carry no enrollment, so they are neither assigned nor self-practice. Charted as their
  // own series so this chart's total matches the per-skill XP totals on the child profile.
  badgeXp: { label: 'Lencana', color: 'var(--chart-3)' },
} satisfies ChartConfig

const SUBJECT_CONFIG = {
  xp: { label: 'XP', color: 'var(--chart-2)' },
} satisfies ChartConfig

/**
 * Learning-independence tiers in spectrum order (most self-directed → least active). Keys match
 * `population.independence`; colors run blue → navy → grey to read as one continuous scale.
 */
const INDEPENDENCE_TIERS = [
  { key: 'highly_self_directed', label: 'Sangat mandiri', className: 'srd:bg-blue-100' },
  { key: 'self_directed', label: 'Mandiri', className: 'srd:bg-blue-50' },
  { key: 'balanced', label: 'Seimbang', className: 'srd:bg-navy-40' },
  { key: 'guided', label: 'Terbimbing', className: 'srd:bg-navy-100' },
  { key: 'inactive', label: 'Kurang aktif', className: 'srd:bg-grey-30' },
] as const

const EMPTY_Y_MAX = 10

function formatDayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
}

function formatXpTick(value: number): string {
  return value === 0 ? '0' : `${value}XP`
}

function toUtcDayKey(date: Date): string {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getDefaultRange(): { from: Date; to: Date } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 28)
  return { from, to }
}

/**
 * One bar per UTC calendar day in [from, to], filling missing days with zero. Bucketed in UTC to
 * match LessonMetricsDaily.date (stored at UTC start-of-day) and the UTC window getLessonTrend
 * applies, so every point the API returns lands on a bar — no edge day gets clipped.
 */
function fillActivityByDayRange(data: ActivityByDay, from: Date, to: Date): ActivityByDay {
  const byDay = new Map<string, { assignedXp: number; selfXp: number; badgeXp: number }>()
  for (const point of data) {
    byDay.set(toUtcDayKey(new Date(point.date)), {
      assignedXp: point.assignedXp,
      selfXp: point.selfXp,
      badgeXp: point.badgeXp,
    })
  }

  const cursor = new Date(from)
  cursor.setUTCHours(0, 0, 0, 0)
  const end = new Date(to)
  end.setUTCHours(0, 0, 0, 0)

  if (end.getTime() < cursor.getTime()) {
    return [{ date: cursor.toISOString(), assignedXp: 0, selfXp: 0, badgeXp: 0 }]
  }

  const result: ActivityByDay = []
  while (cursor.getTime() <= end.getTime()) {
    const key = toUtcDayKey(cursor)
    const day = byDay.get(key)
    result.push({
      date: new Date(cursor).toISOString(),
      assignedXp: day?.assignedXp ?? 0,
      selfXp: day?.selfXp ?? 0,
      badgeXp: day?.badgeXp ?? 0,
    })
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }

  return result
}

/** Round a raw value up to a "nice" 1/2/5 × 10ⁿ step so the axis scales to the data. */
function niceXpStep(rawMax: number, targetTicks = 4): number {
  if (rawMax <= 0) return 1
  const rawStep = rawMax / targetTicks
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const normalized = rawStep / magnitude
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return niceNormalized * magnitude
}

/** Bars are not stacked, so the tallest bar is the largest of the XP series on any day. */
function getYAxisMax(data: ActivityByDay): number {
  const dataMax = data.reduce((max, point) => Math.max(max, point.assignedXp, point.selfXp, point.badgeXp), 0)
  if (dataMax === 0) return EMPTY_Y_MAX
  const step = niceXpStep(dataMax)
  return Math.ceil(dataMax / step) * step
}

function getYTicks(max: number): number[] {
  const step = max <= EMPTY_Y_MAX ? max : niceXpStep(max)
  const ticks: number[] = []
  for (let value = 0; value <= max; value += step) {
    ticks.push(value)
  }
  return ticks
}

function gradeToStars(grade: number | null): 0 | 1 | 2 | 3 {
  if (grade == null) return 0
  return starOnLessonArray({ type: 'graded_xp', gradedXp: grade }).filter((s) => s.isFilled).length as 0 | 1 | 2 | 3
}

function getSubjectChartHeight(rowCount: number): number {
  return Math.max(140, rowCount * 36 + 48)
}

function getSubjectYAxisWidth(data: RouterOutputs['metrics']['getLessonTrend']['subjectCompletion']): number {
  const longestName = data.reduce((max, row) => Math.max(max, row.name.length), 0)
  return Math.min(120, Math.max(72, longestName * 7))
}

/** Round the largest per-subject XP up to a "nice" step so the x-axis scales to the data. */
function getSubjectXMax(maxXp: number): number {
  if (maxXp <= 0) return EMPTY_Y_MAX
  const step = niceXpStep(maxXp)
  return Math.ceil(maxXp / step) * step
}

export function LessonActivityChart({
  data,
  population,
  from,
  to,
}: {
  data: ActivityByDay
  population: Population
  from?: Date
  to?: Date
}) {
  const range = from && to ? { from, to } : getDefaultRange()
  const chartData = fillActivityByDayRange(data, range.from, range.to)
  const yMax = getYAxisMax(chartData)
  const yTicks = getYTicks(yMax)

  const { independence } = population
  const independenceTotal = INDEPENDENCE_TIERS.reduce((sum, tier) => sum + independence[tier.key], 0)

  return (
    <div className='srd:flex srd:flex-1 srd:flex-col srd:gap-2 srd:mt-2'>
      <ChartContainer
        config={ACTIVITY_CONFIG}
        className={cn('srd:min-h-[220px] srd:h-auto', 'srd:aspect-auto srd:w-full srd:shrink-0')}
      >
        <BarChart accessibilityLayer data={chartData} margin={{ left: 0, right: 8, top: 16, bottom: 8 }}>
          <CartesianGrid vertical={false} strokeDasharray='4 4' />
          <XAxis
            dataKey='date'
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={formatDayLabel}
            minTickGap={24}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={48}
            domain={[0, yMax]}
            ticks={yTicks}
            tickFormatter={formatXpTick}
            allowDataOverflow
          />
          <ChartTooltip
            content={<ChartTooltipContent className='srd:min-w-48' labelFormatter={(v) => formatDayLabel(String(v))} />}
          />
          <Bar dataKey='assignedXp' fill='var(--color-assignedXp)' radius={[4, 4, 0, 0]} />
          <Bar dataKey='selfXp' fill='var(--color-selfXp)' radius={[4, 4, 0, 0]} />
          <Bar dataKey='badgeXp' fill='var(--color-badgeXp)' radius={[4, 4, 0, 0]} />
        </BarChart>
      </ChartContainer>

      <div
        className={cn(
          'srd:flex srd:shrink-0 srd:flex-wrap srd:items-center srd:justify-center srd:gap-x-3 srd:gap-y-1',
        )}
      >
        {(
          Object.entries(ACTIVITY_CONFIG) as [
            keyof typeof ACTIVITY_CONFIG,
            (typeof ACTIVITY_CONFIG)[keyof typeof ACTIVITY_CONFIG],
          ][]
        ).map(([key, { label, color }]) => (
          <span
            key={key}
            className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:flex srd:items-center srd:gap-1')}
          >
            <span className='srd:size-2 srd:rounded-full' style={{ backgroundColor: color }} />
            {label}
          </span>
        ))}
      </div>

      <div className='srd:mt-auto srd:flex srd:flex-col srd:gap-2 srd:border-t srd:border-outline-variant/50 srd:pt-3'>
        {/* Kemandirian spectrum: one seamless bar, segment widths ∝ number of children in each tier. */}
        <div className='srd:flex srd:h-3 srd:w-full srd:overflow-hidden srd:rounded-full'>
          {INDEPENDENCE_TIERS.map((tier) => {
            const count = independence[tier.key]
            if (independenceTotal === 0 || count === 0) return null
            return (
              <span
                key={tier.key}
                className={cn('srd:h-full', tier.className)}
                style={{ width: `${(count / independenceTotal) * 100}%` }}
              />
            )
          })}
        </div>

        <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-center srd:gap-x-3 srd:gap-y-1'>
          {INDEPENDENCE_TIERS.map((tier) => (
            <span
              key={tier.key}
              className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:flex srd:items-center srd:gap-1.5')}
            >
              <span className={cn('srd:size-4 srd:rounded-[4px]', tier.className)} />
              <span className={cn(typographyVariants({ variant: 'body-sm' }))}>
                {independence[tier.key].toLocaleString('id-ID')} {tier.label}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

export function SubjectCompletionChart({
  data,
  population,
}: {
  data: RouterOutputs['metrics']['getLessonTrend']['subjectCompletion']
  population: Population
}) {
  const { avgCompletion, avgGrade, gradeStdDev, hasGrades } = population
  const stars = gradeToStars(hasGrades ? avgGrade : null)
  const yAxisWidth = getSubjectYAxisWidth(data)
  const chartHeight = getSubjectChartHeight(Math.max(data.length, 1))

  // Recharts draws the first data point at the bottom in a vertical layout, so sort XP ascending to
  // put the highest-earning subject at the top. Bar value = total earned XP per subject.
  const sortedData = [...data].sort((a, b) => a.xp - b.xp)
  const xMax = getSubjectXMax(sortedData.reduce((max, row) => Math.max(max, row.xp), 0))
  const xTicks = getYTicks(xMax)

  return (
    <div className='srd:flex srd:flex-1 srd:flex-col srd:gap-2 srd:mt-2'>
      <ChartContainer
        config={SUBJECT_CONFIG}
        className='srd:aspect-auto srd:w-full srd:flex-1'
        style={{ minHeight: chartHeight }}
      >
        <BarChart
          accessibilityLayer
          data={sortedData}
          layout='vertical'
          barCategoryGap={20}
          barSize={14}
          margin={{ left: 0, right: 12, top: 4, bottom: 0 }}
        >
          <CartesianGrid horizontal={false} strokeDasharray='4 4' />
          <XAxis
            type='number'
            domain={[0, xMax]}
            ticks={xTicks}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={formatXpTick}
          />
          <YAxis
            type='category'
            dataKey='name'
            tickLine={false}
            axisLine={false}
            width={yAxisWidth}
            tick={{ fill: 'var(--grey-100)' }}
          />
          <ChartTooltip content={<ChartTooltipContent className='srd:min-w-48' />} />
          <Bar
            dataKey='xp'
            fill='var(--color-xp)'
            radius={[0, 999, 999, 0]}
            minPointSize={2}
            // Explicit shape (single color per subject) — matches the chart's original bar rendering.
            shape={(props: BarShapeProps) => <Rectangle {...props} fill='var(--color-xp)' />}
          />
        </BarChart>
      </ChartContainer>

      <div className='srd:mt-auto srd:border-t srd:border-outline-variant/50 srd:pt-2 srd:gap-4 srd:flex srd:flex-wrap'>
        <div className='srd:flex srd:flex-col srd:gap-y-1'>
          <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>Rata-rata selesai</p>
          <NumberFlow
            value={avgCompletion}
            suffix='%'
            format={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
            className={cn(typographyVariants({ variant: 'body-md' }), 'srd:font-bold')}
          />
        </div>
        <div className='srd:flex srd:flex-col srd:gap-y-1'>
          <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>Rata-rata nilai</p>
          <p
            className={cn(
              typographyVariants({ variant: 'body-sm' }),
              'srd:flex srd:flex-wrap srd:items-center srd:gap-x-2',
            )}
          >
            {hasGrades && <RewardStar stars={stars} className='srd:w-13' middleClassName='srd:-translate-y-1' />}
            <span className={cn(typographyVariants({ variant: 'body-md' }), 'srd:font-bold')}>
              {hasGrades
                ? `${Math.round(avgGrade).toLocaleString('id-ID')} (±${gradeStdDev.toLocaleString('id-ID', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 1,
                  })})`
                : '-'}
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}
